import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Tag, Image } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CrudPage from '../../../components/common/CrudPage';
import { profileService } from '../../../services/next';

dayjs.extend(customParseFormat);

const { Text } = Typography;

// Chuyển từ cms.next.lichviet.org/profiles ("Quản lý thành viên", LichVietOld\ProfileListController).
// Bảng `lichviet.profiles` — thành viên gia đình người dùng tạo trên app (dữ liệu cá nhân).
// `birthday` lưu chuỗi DD/MM/YYYY; `birth_time` "1".."12"; app xoá bằng status = 0.

const QUERY_KEY = 'profileList';
const CDN_BASE = 'https://cdn.lichviet.org';

const GENDER_OPTIONS = [
  { label: 'Nam', value: 'Nam' },
  { label: 'Nữ', value: 'Nữ' },
];

const BIRTH_TIME_OPTIONS = [
  'Tý (23h - 1h)',
  'Sửu (1h - 3h)',
  'Dần (3h - 5h)',
  'Mão (5h - 7h)',
  'Thìn (7h - 9h)',
  'Tị (9h - 11h)',
  'Ngọ (11h - 13h)',
  'Mùi (13h - 15h)',
  'Thân (15h - 17h)',
  'Dậu (17h - 19h)',
  'Tuất (19h - 21h)',
  'Hợi (21h - 23h)',
].map((label, i) => ({ label, value: String(i + 1) }));

// Danh sách của CMS cũ + "Con", "Cháu" (app đang ghi, có nhiều trên production). Vẫn gõ được giá trị khác.
const RELATIONSHIP_OPTIONS = [
  'Cụ', 'Ông', 'Bà', 'Bác', 'Bố', 'Mẹ', 'Vợ', 'Chồng', 'Con', 'Cháu', 'Cô/Dì', 'Chú/Cậu', 'Anh', 'Chị', 'Em', 'Khác',
].map((v) => ({ label: v, value: v }));

const STATUS_OPTIONS = [
  { label: 'Đang dùng', value: 1 },
  { label: 'Người dùng đã xoá', value: 0 },
];

const toCdnUrl = (path) => {
  if (!path) return '';
  if (/^(https?:|data:)/i.test(path)) return path;
  return `${CDN_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
};

const birthTimeLabel = (v) => BIRTH_TIME_OPTIONS.find((o) => o.value === String(v))?.label || '';

const QuanLyThanhVienGiaDinh = () => {
  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
      {
        title: 'ẢNH',
        dataIndex: 'avatar',
        key: 'avatar',
        width: 70,
        render: (a) => (a ? <Image src={toCdnUrl(a)} width={40} height={40} style={{ objectFit: 'cover' }} /> : '-'),
      },
      { title: 'TÊN THÀNH VIÊN', dataIndex: 'name', key: 'name', width: 180, render: (t) => <Text strong>{t}</Text> },
      { title: 'GIỚI TÍNH', dataIndex: 'gender', key: 'gender', width: 90, render: (g) => g || '-' },
      { title: 'QUAN HỆ', dataIndex: 'relationship', key: 'relationship', width: 100, render: (r) => r || '-' },
      { title: 'NGÀY SINH', dataIndex: 'birthday', key: 'birthday', width: 110, render: (b) => b || '-' },
      { title: 'GIỜ SINH', dataIndex: 'birth_time', key: 'birth_time', width: 130, render: (v) => birthTimeLabel(v) || '-' },
      {
        title: 'TUỔI / MỆNH',
        dataIndex: 'can_chi_tuoi',
        key: 'can_chi_tuoi',
        width: 150,
        render: (c, r) => [c, r.menh].filter(Boolean).join(' · ') || '-',
      },
      {
        title: 'USER_ID',
        dataIndex: 'create_by',
        key: 'create_by',
        width: 110,
        render: (u) => (u ? <Link to={`/cms/quan-ly-user/${u}`}>{u}</Link> : '-'),
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (s) => (Number(s) === 1 ? <Tag color="green">Đang dùng</Tag> : <Tag>Người dùng đã xoá</Tag>),
      },
      {
        title: 'NGÀY TẠO',
        dataIndex: 'create_time',
        key: 'create_time',
        width: 140,
        render: (t) => (t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-'),
      },
    ],
    []
  );

  const formFields = useCallback(
    ({ isEditMode }) => [
      { name: 'name', label: 'Tên thành viên', type: 'input', span: 24, rules: [{ required: true, whitespace: true }] },
      // App bắt buộc Nam/Nữ khi người dùng sửa thành viên — để trống thì họ không sửa được trên app.
      { name: 'gender', label: 'Giới tính', type: 'select', options: GENDER_OPTIONS, span: 12, rules: [{ required: true }] },
      {
        name: 'relationship',
        label: 'Quan hệ',
        type: 'select',
        options: RELATIONSHIP_OPTIONS,
        span: 12,
        propsInput: { mode: 'tags', maxCount: 1, allowClear: true, placeholder: 'Chọn hoặc gõ quan hệ' },
      },
      {
        name: 'birthday',
        label: 'Ngày sinh (dương lịch)',
        type: 'date',
        span: 12,
        format: 'DD/MM/YYYY',
        submitFormat: 'YYYY-MM-DD',
        rules: [{ required: true }],
      },
      {
        name: 'birth_time',
        label: 'Giờ sinh',
        type: 'select',
        options: BIRTH_TIME_OPTIONS,
        span: 12,
        propsInput: { allowClear: true, placeholder: 'Không rõ' },
      },
      {
        name: 'avatar',
        label: 'Ảnh',
        type: 'upload-cdn',
        span: 24,
        uploadFolder: 'users',
        accept: 'image/*',
        enterLink: true,
        linkPlaceholder: 'Link ảnh',
      },
      {
        name: 'create_by',
        label: 'user_id',
        type: 'input',
        span: 12,
        rules: isEditMode ? [] : [{ required: true, pattern: /^\d+$/, message: 'Nhập user_id (số)' }],
        propsInput: { disabled: isEditMode },
        propsFormItem: { extra: isEditMode ? 'Không đổi được chủ sở hữu (như CMS cũ).' : 'Thành viên được thêm vào tài khoản này.' },
      },
    ],
    []
  );

  // `birthday` lưu DD/MM/YYYY nên CrudPage không tự đọc được; `relationship` là ô tags 1 giá trị.
  const mapDataToForm = useCallback(
    (record) => {
      const birthday = record.birthday ? dayjs(record.birthday, ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD'], true) : null;
      return {
        birthday: birthday && birthday.isValid() ? birthday : undefined,
        birth_time: record.birth_time ? String(record.birth_time) : undefined,
        relationship: record.relationship ? [record.relationship] : [],
        create_by: record.create_by,
      };
    },
    []
  );

  const mapFormToData = useCallback(
    (payload) => ({
      relationship: Array.isArray(payload.relationship) ? payload.relationship[0] || null : payload.relationship || null,
      birth_time: payload.birth_time || null,
    }),
    []
  );

  const filterFields = useMemo(
    () => [
      { label: 'user_id (nhiều id cách nhau dấu phẩy)', value: 'create_by', type: 'input' },
      { label: 'Giới tính', value: 'gender', options: [{ label: 'Tất cả', value: '' }, ...GENDER_OPTIONS] },
      { label: 'Trạng thái', value: 'status', options: [{ label: 'Tất cả', value: '' }, ...STATUS_OPTIONS] },
    ],
    []
  );

  return (
    <CrudPage
      title="THÀNH VIÊN GIA ĐÌNH"
      subTitle="Thành viên gia đình người dùng tạo trên app (bảng profiles) — dữ liệu cá nhân, chỉ dùng cho hỗ trợ khách hàng"
      itemsName="Thành viên"
      queryKey={QUERY_KEY}
      service={profileService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm theo tên thành viên..."
      addButtonLabel="Thêm thành viên"
      deleteTitle="Xóa thành viên này?"
      deleteDesc="Xoá vĩnh viễn như CMS cũ, không khôi phục được."
      disableCopy
      formConfig={{ width: 720 }}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLyThanhVienGiaDinh;
